import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

// Read admin auth token from any storage the rest of /gestione uses.
// Historically the admin login saves it as sessionStorage['access_token'];
// fall back to the older keys for safety.
const getAdminToken = () =>
  sessionStorage.getItem("access_token") ||
  sessionStorage.getItem("token") ||
  localStorage.getItem("access_token") ||
  localStorage.getItem("admin_token") ||
  "";

/**
 * Admin widget — generates a custom payment link (no shipping, no catalog
 * product) ready to be sent via WhatsApp/email to a customer.
 * Shows the last 10 generated payments with live status badges.
 */
export const CustomPaymentLinkWidget = () => {
  const [amount, setAmount] = useState("");
  const [causale, setCausale] = useState("");
  const [validityDays, setValidityDays] = useState("7"); // default: 7 days
  const [generated, setGenerated] = useState(null);
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const token = getAdminToken();
      const { data } = await axios.get(`${API}/admin/custom-payments?limit=10`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setHistory(data.payments || []);
    } catch (err) {
      // Silent — widget keeps working even without history
      console.warn("custom-payment history load failed", err);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const handleGenerate = async () => {
    const a = parseFloat(amount.replace(",", "."));
    if (!Number.isFinite(a) || a <= 0) {
      toast.error("Inserisci un importo valido (es. 12.50)");
      return;
    }
    if (!causale.trim()) {
      toast.error("Inserisci una causale");
      return;
    }
    setLoading(true);
    try {
      const token = getAdminToken();
      const days = parseInt(validityDays, 10);
      const { data } = await axios.post(
        `${API}/admin/custom-payment/generate-link`,
        {
          amount: a,
          causale: causale.trim(),
          validity_days: Number.isFinite(days) && days >= 0 ? Math.min(days, 365) : 7,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setGenerated(data);
      toast.success("Link generato!");
    } catch (err) {
      const msg = err.response?.data?.detail || err.message;
      toast.error(typeof msg === "string" ? msg : "Errore generazione link");
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Link copiato negli appunti");
  };

  const buildPayUrl = (item) => {
    const params = new URLSearchParams({ i: Number(item.amount).toFixed(2), c: item.causale });
    if (item.customer_name) params.set("n", item.customer_name);
    if (item.customer_email) params.set("e", item.customer_email);
    return `${BACKEND_URL}/paga?${params.toString()}`;
  };

  const handleWhatsApp = (item) => {
    const url = item.url || buildPayUrl(item);
    const amt = Number(item.amount).toFixed(2).replace(".", ",");
    const causaleText = item.causale || "Pagamento";
    const msg = `Ciao! Per completare il pagamento di € ${amt} (${causaleText}), clicca qui:\n${url}\n\nGrazie,\nEdicola Cerasuolo`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
  };

  const handleResend = async (sessionId) => {
    if (!window.confirm("Reinviare email admin + ricevuta cliente per questo pagamento?")) return;
    try {
      const token = getAdminToken();
      const { data } = await axios.post(
        `${API}/admin/custom-payment/resend-notification/${sessionId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (data.sent_admin && data.sent_customer) {
        toast.success("✅ Email reinviate: admin + cliente");
      } else if (data.sent_admin) {
        toast.warning("⚠️ Email admin inviata, ma cliente fallita");
      } else if (data.sent_customer) {
        toast.warning("⚠️ Email cliente inviata, ma admin fallita");
      } else {
        toast.error("❌ Invio email fallito (controlla SMTP)");
      }
    } catch (err) {
      const msg = err.response?.data?.detail || err.message;
      toast.error(typeof msg === "string" ? msg : "Errore reinvio email");
    }
  };

  const reset = () => {
    setAmount("");
    setCausale("");
    // Keep validityDays so the admin doesn't have to re-set their habit
    setGenerated(null);
    loadHistory(); // refresh the history after a new generation
  };

  const statusBadge = (s) => {
    const map = {
      paid: { label: "✅ Pagato", cls: "bg-green-100 text-green-800 border-green-200" },
      unpaid: { label: "⏳ Non pagato", cls: "bg-amber-100 text-amber-800 border-amber-200" },
      pending: { label: "⏳ In attesa", cls: "bg-amber-100 text-amber-800 border-amber-200" },
      expired: { label: "⌛ Scaduto", cls: "bg-gray-100 text-gray-600 border-gray-200" },
      canceled: { label: "❌ Annullato", cls: "bg-red-100 text-red-700 border-red-200" },
    };
    const m = map[s] || map.pending;
    return <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium border ${m.cls}`}>{m.label}</span>;
  };

  const formatDate = (d) => {
    if (!d) return "—";
    try {
      const dt = typeof d === "string" ? new Date(d) : d;
      return dt.toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
    } catch {
      return String(d);
    }
  };

  return (
    <Card data-testid="custom-payment-link-widget">
      <CardHeader>
        <CardTitle className="flex items-center justify-between gap-2 text-base">
          <span>💸 Genera Link Pagamento Personalizzato</span>
          <Button
            size="sm"
            variant="ghost"
            onClick={loadHistory}
            disabled={historyLoading}
            data-testid="cp-refresh-btn"
            className="text-xs"
          >
            {historyLoading ? "..." : "🔄 Aggiorna"}
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {!generated ? (
          <>
            <p className="text-sm text-gray-600">
              Crea un link da inviare al cliente per pagare un importo specifico (es. differenza spedizione,
              pagamento parziale, servizio custom). <strong>Nessuna spedizione</strong> applicata.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label htmlFor="cp-amount">Importo (€) *</Label>
                <Input
                  id="cp-amount"
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="12.50"
                  data-testid="cp-amount-input"
                />
              </div>
              <div className="sm:col-span-1">
                <Label htmlFor="cp-causale">Causale *</Label>
                <Input
                  id="cp-causale"
                  value={causale}
                  onChange={(e) => setCausale(e.target.value)}
                  placeholder="Differenza spedizione #1234"
                  data-testid="cp-causale-input"
                />
              </div>
              <div>
                <Label htmlFor="cp-validity">Validità (giorni)</Label>
                <Input
                  id="cp-validity"
                  type="number"
                  min="0"
                  max="365"
                  value={validityDays}
                  onChange={(e) => setValidityDays(e.target.value)}
                  placeholder="7 (0 = mai)"
                  data-testid="cp-validity-input"
                />
              </div>
            </div>
            <Button
              onClick={handleGenerate}
              disabled={loading}
              className="w-full bg-amber-500 hover:bg-amber-600"
              data-testid="cp-generate-btn"
            >
              {loading ? "Generazione..." : "🔗 Genera Link"}
            </Button>
          </>
        ) : (
          <>
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <div className="text-xs font-medium text-green-700 mb-1">
                ✅ Link generato per € {generated.amount.toFixed(2).replace(".", ",")} — {generated.causale}
              </div>
              {generated.validity_days > 0 ? (
                <div className="text-xs text-amber-700 mb-2">
                  ⌛ Scade tra {generated.validity_days} {generated.validity_days === 1 ? "giorno" : "giorni"}
                </div>
              ) : (
                <div className="text-xs text-gray-500 mb-2">⚪ Senza scadenza</div>
              )}
              <div className="text-xs font-medium text-gray-700 mt-2 mb-1">📎 Link breve (cliccabile su WhatsApp)</div>
              <div className="text-sm text-blue-700 break-all font-mono bg-white p-2 rounded border border-blue-200 select-all" data-testid="cp-generated-short-url">
                {generated.short_url || generated.url}
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <Button onClick={() => handleCopy(generated.short_url || generated.url)} variant="outline" data-testid="cp-copy-btn">
                📋 Copia link breve
              </Button>
              <Button
                onClick={() => handleWhatsApp({
                  url: generated.short_url || generated.url,
                  amount: generated.amount,
                  causale: generated.causale,
                })}
                className="bg-green-600 hover:bg-green-700 text-white"
                data-testid="cp-whatsapp-btn"
              >
                💬 Invia su WhatsApp
              </Button>
              <Button onClick={reset} variant="outline" data-testid="cp-reset-btn">
                ➕ Nuovo link
              </Button>
            </div>
          </>
        )}

        {/* History — ultimi 10 pagamenti */}
        {history.length > 0 && (
          <div className="border-t pt-3 mt-3">
            <div className="text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">
              Ultimi {history.length} pagamenti
            </div>
            <div className="space-y-2 max-h-72 overflow-y-auto pr-1" data-testid="cp-history-list">
              {history.map((item) => (
                <div
                  key={item.id || item.session_id}
                  className="flex items-center justify-between gap-2 p-2.5 bg-gray-50 hover:bg-gray-100 rounded-lg border border-gray-200 transition"
                  data-testid={`cp-history-item-${item.session_id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="font-semibold text-gray-900 text-sm">
                        € {Number(item.amount).toFixed(2).replace(".", ",")}
                      </span>
                      {statusBadge(item.payment_status)}
                    </div>
                    <div className="text-xs text-gray-600 truncate" title={item.causale}>
                      {item.causale}
                    </div>
                    <div className="text-xs text-gray-400 mt-0.5">
                      {item.customer_name || "—"} · {formatDate(item.created_at)}
                    </div>
                  </div>
                  <div className="flex flex-col gap-1 shrink-0">
                    {item.payment_status === "paid" && (
                      <button
                        onClick={() => handleResend(item.session_id)}
                        className="text-xs bg-blue-500 hover:bg-blue-600 text-white px-2 py-1 rounded transition"
                        data-testid={`cp-history-resend-${item.session_id}`}
                        title="Reinvia email admin + cliente"
                      >
                        ✉️
                      </button>
                    )}
                    <button
                      onClick={() => handleWhatsApp(item)}
                      className="text-xs bg-green-500 hover:bg-green-600 text-white px-2 py-1 rounded transition"
                      data-testid={`cp-history-whatsapp-${item.session_id}`}
                      title="Reinvia su WhatsApp"
                    >
                      💬
                    </button>
                    <button
                      onClick={() => handleCopy(buildPayUrl(item))}
                      className="text-xs bg-gray-200 hover:bg-gray-300 text-gray-700 px-2 py-1 rounded transition"
                      data-testid={`cp-history-copy-${item.session_id}`}
                      title="Copia link"
                    >
                      📋
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
